import React, { useEffect, useRef, useState } from 'react';
import { useMarketProjections } from '../../../hooks/useMarketProjections';
import { useTamEstimation } from '../../../hooks/useTamEstimation';
import { Loader2, HelpCircle, DollarSign, TrendingUp, Receipt, Settings, CheckCircle, AlertCircle, Sparkles, Lightbulb, ChevronDown, ChevronRight } from 'lucide-react';
import { ValuationData, ValidationErrors } from '../../../types/valuation';
import { Tooltip } from '../../ui/Tooltip';
import { EntrepreneurialSynergyBox } from '../EntrepreneurialSynergyBox';
import { TamSuggestion, TamDiagnostics } from '../TamProjectionSuggestions';
import { defaultSynergyAnswers } from '../../../types/entrepreneurial-synergy';
import { supabase } from '../../../lib/supabase';
import { logPendingDataProperty, checkIfPropertyAlreadyLogged } from '../../../services/pendingDataService';
import InsufficientDataModal from '../InsufficientDataModal';
import clsx from 'clsx';

interface FinancialMetricsProps {
  data: ValuationData;
  onUpdate: (data: Partial<ValuationData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  errors?: ValidationErrors;
  // Data flywheel callbacks
  logTamAccept?: (fieldName: string, suggestedValue: number, currentUserValue: number) => void;
  logTamDismiss?: (fieldName: string, suggestedValue: number, userValue: number) => void;
  checkModifiedAfterAccept?: (fieldName: string, newValue: number) => void;
}

type ReportingType = 'historical' | 'projected';
type ReportingPeriod = 'monthly' | 'annual';
type SectionType = 'revenue' | 'expenses' | 'synergy';

const inputClasses = {
  base: 'w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
  withPrefix: 'pl-7',
  withSuffix: 'pr-7',
  error: 'border-red-500',
  default: 'border-gray-300',
  readOnly: 'bg-gray-100'
};

interface MoneyInputProps {
  id: string;
  label: string;
  tooltip: string;
  value: number | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  readOnly?: boolean;
  className?: string;
  error?: boolean;
  hint?: string;
}

const MoneyInput = ({ id, label, tooltip, value, onChange, placeholder, readOnly, className, error, hint }: MoneyInputProps) => {
  const [localValue, setLocalValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused && value) {
      setLocalValue(value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    } else if (!isFocused && !value) {
      setLocalValue('');
    }
  }, [value, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, '');
    setLocalValue(rawValue);
    const normalized = rawValue.replace(',', '.');
    const numericValue = normalized ? parseFloat(normalized) : 0;
    onChange({ ...e, target: { ...e.target, value: numericValue.toString() } });
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (value) {
      setLocalValue(value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    } else {
      setLocalValue('');
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value ? value.toString() : '');
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
        <Tooltip content={tooltip}>
          <HelpCircle className="w-4 h-4 text-gray-400" />
        </Tooltip>
      </div>
      <div className="relative">
        <span className="absolute left-3 top-3 text-gray-500">$</span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          pattern="^[0-9]*[.,]?[0-9]{0,2}$"
          value={localValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={clsx(
            inputClasses.base,
            inputClasses.withPrefix,
            readOnly ? inputClasses.readOnly : undefined,
            error ? 'border-red-500 ring-1 ring-red-500' : undefined
          )}
          placeholder={placeholder}
          readOnly={readOnly === true}
        />
      </div>
      {error && (
        <p className="text-xs text-red-600 mt-1">Margin is required when volume is entered</p>
      )}
      {hint && !error && (
        <p className="text-xs text-gray-400 mt-1">{hint}</p>
      )}
    </div>
  );
};

interface PercentageInputProps {
  id: string;
  label: string;
  tooltip: string;
  value: number | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  className?: string;
  readOnly?: boolean;
  error?: boolean;
  hint?: string;
}

const PercentageInput = ({ id, label, tooltip, value, onChange, placeholder, className, readOnly, error, hint }: PercentageInputProps) => (
  <div className={className}>
    <div className="flex items-center gap-2 mb-2">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
      <Tooltip content={tooltip}>
        <HelpCircle className="w-4 h-4 text-gray-400" />
      </Tooltip>
    </div>
    <div className="relative">
      <input
        id={id}
        type="number"
        step="1"
        min="0"
        max="100"
        value={value ? Math.round(value * 100) : ''}
        onChange={onChange}
        className={clsx(
          inputClasses.base,
          inputClasses.withSuffix,
          readOnly ? inputClasses.readOnly : undefined,
          error ? 'border-red-500 ring-1 ring-red-500' : undefined
        )}
        readOnly={readOnly === true}
        placeholder={placeholder}
      />
      <span className="absolute right-3 top-3 text-gray-500">%</span>
    </div>
    {error && (
      <p className="text-xs text-red-600 mt-1">Margin is required when revenue is entered</p>
    )}
    {hint && !error && (
      <p className="text-xs text-gray-400 mt-1">{hint}</p>
    )}
  </div>
);

interface VolumeInputProps {
  id: string;
  label: string;
  tooltip: string;
  value: number | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  className?: string;
  readOnly?: boolean;
  unit?: string; 
}

const VolumeInput = ({ id, label, tooltip, value, onChange, placeholder, className, readOnly, unit = "" }: VolumeInputProps) => {
  const [localValue, setLocalValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused && value) {
      setLocalValue(value.toLocaleString('en-US'));
    } else if (!isFocused && !value) {
      setLocalValue('');
    }
  }, [value, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, '');
    setLocalValue(rawValue);
    const normalized = rawValue.replace(',', '.');
    const numericValue = normalized ? parseFloat(normalized) : 0;
    onChange({ ...e, target: { ...e.target, value: numericValue.toString() } });
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (value) {
      setLocalValue(value.toLocaleString('en-US'));
    } else {
      setLocalValue('');
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value ? value.toString() : '');
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
        <Tooltip content={tooltip}>
          <HelpCircle className="w-4 h-4 text-gray-400" />
        </Tooltip>
      </div>
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          pattern="^[0-9]*[.,]?[0-9]*$"
          value={localValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={clsx(inputClasses.base, unit ? inputClasses.withSuffix : undefined, readOnly ? inputClasses.readOnly : undefined)}
          placeholder={placeholder}
          readOnly={readOnly === true}
        />
        {unit && <span className="absolute right-3 top-3 text-gray-500">{unit}</span>}
      </div>
    </div>
  );
};

const parseValue = (value: string): number => {
  const cleanValue = value.replace(/[^0-9.-]/g, '');
  return cleanValue ? parseFloat(cleanValue) : 0;
};

export default function FinancialMetrics({ data, onUpdate, onNext, onPrevious, errors, logTamAccept, logTamDismiss, checkModifiedAfterAccept }: FinancialMetricsProps) {
  const { projections, estimatedRevenue, isLoading, error, fetchProjections } = useMarketProjections();
  const { isLoading: tamLoading, error: tamError, result: tamResult, fetchTamEstimation } = useTamEstimation();
  const [reportingType, setReportingType] = useState<ReportingType>('historical');
  const [reportingPeriod, setReportingPeriod] = useState<ReportingPeriod>('annual');
  const [isManualGrossProfit, setIsManualGrossProfit] = useState(false);
  const [isManualOperatingExpenses, setIsManualOperatingExpenses] = useState(false);
  const [stationData, setStationData] = useState<any>(null);
  const [showInsufficientDataModal, setShowInsufficientDataModal] = useState(false);

  // Data flywheel: track which TAM fields have been shown (for dismiss detection)
  const shownTamFields = useRef<Set<string>>(new Set());

  // Accordion state - track which sections are expanded (synergy open by default)
  const [expandedSections, setExpandedSections] = useState<Set<SectionType>>(new Set(['revenue', 'synergy']));
  const [showExpenseExample, setShowExpenseExample] = useState(false);
  const [showOpexGuide, setShowOpexGuide] = useState(false);

  const toggleSection = (section: SectionType) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const sections = [
    {
      id: 'revenue' as SectionType,
      label: 'Revenue Streams',
      icon: DollarSign,
      completed: false,
      description: 'Enter fuel and retail revenue data',
      required: true
    },
    {
      id: 'expenses' as SectionType,
      label: 'Operating Expenses',
      icon: Receipt,
      completed: false,
      description: 'Enter operational cost data',
      required: true
    },
    {
      id: 'synergy' as SectionType,
      label: 'Operating Advantages',
      icon: Sparkles,
      completed: false,
      description: 'Estimate your value creation potential',
      required: false
    }
  ];

  useEffect(() => {
    if (projections) {
      onUpdate({
        annualGallons: projections.estimated_annual_fuel_volume,
        storeRevenue: estimatedRevenue?.estimatedCStoreRevenue,
      });
    }
  }, [projections, estimatedRevenue, onUpdate]);

  const handleProjectionClick = () => {
    fetchProjections(data.stationObjectId || 1);
  };

  // Fetch station data when stationObjectId is available
  useEffect(() => {
    const fetchStationData = async () => {
      if (data.stationObjectId) {
        const result = await supabase.public.fetchStationById(data.stationObjectId);
        if (result.data) {
          setStationData(result.data);
        }
      }
    };
    fetchStationData();
  }, [data.stationObjectId]);

  // Handle TAM projection click
  const handleTamProjectionClick = async () => {
    console.log('TAM Projection clicked - stationObjectId:', data.stationObjectId);

    // If we have a stationObjectId, send it to the Edge Function
    // The Edge Function will query the private schema itself using the service role key
    if (data.stationObjectId) {
      console.log('Sending station_object_id to Edge Function:', data.stationObjectId);

      try {
        await fetchTamEstimation({
          station_object_id: data.stationObjectId,
          // User-provided values take priority over database values
          fuel_brand: data.brandName || null,
          store_brand: data.storeBrand || null,
        });
      } catch (error: any) {
        console.error('TAM estimation failed:', error);

        // If the error is due to insufficient data, show modal and log the property
        const errorMessage = error?.message || error?.error || 'Unknown error';

        // Check if this is an insufficient data error (you may need to adjust this check based on actual error messages)
        const isInsufficientDataError =
          errorMessage.toLowerCase().includes('insufficient') ||
          errorMessage.toLowerCase().includes('not enough') ||
          errorMessage.toLowerCase().includes('missing') ||
          errorMessage.toLowerCase().includes('no data');

        if (isInsufficientDataError) {
          // Check if we've already logged this property
          const alreadyLogged = await checkIfPropertyAlreadyLogged(
            data.stationObjectId,
            data.location
          );

          if (!alreadyLogged) {
            // Log the property
            await logPendingDataProperty({
              stationObjectId: data.stationObjectId,
              propertyAddress: data.location || undefined,
              locationData: undefined, // Location details not available in ValuationData
              errorMessage: errorMessage
            });
          }

          // Show the user-friendly modal
          setShowInsufficientDataModal(true);
        }
      }
      return;
    }

    // Fallback: No stationObjectId - would need all manual inputs
    // This path requires the user to have entered all required data manually
    console.warn('No stationObjectId available. Manual TAM estimation requires complete data entry.');

    // Show modal and log this too
    await logPendingDataProperty({
      propertyAddress: data.location || undefined,
      locationData: undefined, // Location details not available in ValuationData
      errorMessage: 'No database property selected - insufficient data for projections'
    });

    setShowInsufficientDataModal(true);
  };

  // Data flywheel: mark TAM fields as shown when TAM results arrive
  useEffect(() => {
    if (tamResult) {
      shownTamFields.current = new Set([
        'annualGallons', 'fuelMargin', 'storeRevenue', 'storeMargin', 'operatingExpenses'
      ]);
    }
  }, [tamResult]);

  // TAM value lookup for dismiss detection
  const getTamSuggestedValue = (fieldName: string): number | undefined => {
    if (!tamResult) return undefined;
    const map: Record<string, number> = {
      annualGallons: tamResult.estimates.fuel_volume_gal,
      fuelMargin: tamResult.estimates.fuel_profit_margin_per_gal,
      storeRevenue: tamResult.estimates.cstore_sales,
      storeMargin: tamResult.estimates.cstore_margin_pct / 100,
      operatingExpenses: (data.totalGrossProfit || 0) * (tamResult.estimates.opex_over_gp_pct / 100),
    };
    return map[fieldName];
  };

  const handleFinancialInputChange = (field: keyof ValuationData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseValue(e.target.value);
    if (field === 'totalGrossProfit') setIsManualGrossProfit(true);
    if (field === 'operatingExpenses') setIsManualOperatingExpenses(true);

    // Data flywheel: check for modified-after-accept
    checkModifiedAfterAccept?.(field as string, value);

    // Data flywheel: detect TAM dismiss (user typed manually instead of accepting)
    if (tamResult && shownTamFields.current.has(field as string)) {
      const suggestedValue = getTamSuggestedValue(field as string);
      if (suggestedValue !== undefined && value !== suggestedValue) {
        logTamDismiss?.(field as string, suggestedValue, value);
        shownTamFields.current.delete(field as string); // Only log dismiss once per field
      }
    }

    onUpdate({ [field]: value });
  };

  const handleMarginChange = (field: keyof ValuationData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseValue(e.target.value) / 100;
    onUpdate({ [field]: value });
  };

  const handlePeriodChange = (period: ReportingPeriod) => setReportingPeriod(period);
  const handleTypeChange = (type: ReportingType) => setReportingType(type);

  const annualToMonthly = (value: number | undefined) => (value || 0) / 12;
  const monthlyToAnnual = (value: number | undefined) => (value || 0) * 12;
  const getPeriodValue = (value: number | undefined) => reportingPeriod === 'annual' ? value : annualToMonthly(value);

  const getPeriodLabel = () => (reportingPeriod === 'annual' ? 'Annual' : 'Monthly');

  useEffect(() => {
    if (isManualGrossProfit) return;
    const fuelProfit = (data.annualGallons || 0) * (data.fuelMargin || 0);
    const storeProfit = (data.storeRevenue || 0) * (data.storeMargin || 0);
    const carWashProfit = data.hasCarWash ? (data.carWashRevenue || 0) * (data.carWashMargin || 0) : 0;
    const qsrRepairBayProfit = (data.qsrRepairBayRevenue || 0) * (data.qsrRepairBayMargin || 0);

    // Calculate QSR leased revenue based on expense type
    let qsrLeasedRevenue = 0;
    if (data.hasLeasedQSR && data.qsrCurrentMonthlyRent) {
      const annualRent = data.qsrCurrentMonthlyRent * 12;
      if (data.qsrExpenseType === 'NNN') {
        // NNN: Full rent is revenue (tenant pays all expenses)
        qsrLeasedRevenue = annualRent;
      } else if (data.qsrExpenseType === 'MG') {
        // Modified Gross: Rent minus landlord expenses
        const landlordExpenses = data.qsrAnnualLandlordExpenses || 0;
        qsrLeasedRevenue = annualRent - landlordExpenses;
      }
      // Store the calculated value back to the data object
      onUpdate({ qsrLeasedRevenue });
    }

    // QSR leased rent is intentionally excluded from Total Gross Profit so
    // the OpEx suggestion math, NOI display, and the (now-ignored) regressor
    // payload all reflect operating profit only. The rent stream is valued
    // separately as a leased fee asset in ValuationResult.
    // qsrLeasedRevenue is still persisted via the onUpdate above for
    // payload backward compatibility and the leased-fee calculation.
    const totalProfit = fuelProfit + storeProfit + carWashProfit + qsrRepairBayProfit;
    if (totalProfit > 0) onUpdate({ totalGrossProfit: totalProfit });
  }, [
    isManualGrossProfit, data.annualGallons, data.fuelMargin, data.storeRevenue,
    data.storeMargin, data.hasCarWash, data.carWashRevenue, data.carWashMargin,
    data.qsrRepairBayRevenue, data.qsrRepairBayMargin,
    data.hasLeasedQSR, data.qsrCurrentMonthlyRent, data.qsrExpenseType,
    data.qsrAnnualLandlordExpenses, onUpdate
  ]);

  useEffect(() => {
    if (isManualOperatingExpenses) return;
    const subtotal = (
      (data.utilities || 0) + (data.salaries || 0) + (data.insurance || 0) + 
      (data.repairsAndMaintenance || 0) + (data.propertyTax || 0) + 
      (data.advertisingAndPromotion || 0) + (data.merchantFees || 0) + 
      (data.generalAndAdmin || 0)
    );
    if (subtotal > 0) onUpdate({ operatingExpenses: subtotal });
  }, [
    isManualOperatingExpenses, data.utilities, data.salaries, data.insurance, 
    data.repairsAndMaintenance, data.propertyTax, data.advertisingAndPromotion, 
    data.merchantFees, data.generalAndAdmin, onUpdate
  ]);

  const getSectionCompletionStatus = (): { [key in SectionType]: boolean } => {
    const hasRevenue = (data.annualGallons || 0) > 0 || (data.storeRevenue || 0) > 0;
    const hasExpenses = (data.operatingExpenses || 0) > 0;
    // Synergy section is always considered "complete" since it's optional
    const hasSynergy = true;

    return {
      revenue: hasRevenue,
      expenses: hasExpenses,
      synergy: hasSynergy
    };
  };

  const completionStatus = getSectionCompletionStatus();
  // Only require revenue and expenses for completion (synergy is optional)
  const requiredSectionsComplete = completionStatus.revenue && completionStatus.expenses;
  const allSectionsComplete = requiredSectionsComplete;
  const completedSectionsCount = [completionStatus.revenue, completionStatus.expenses].filter(Boolean).length;

  sections.forEach(section => section.completed = completionStatus[section.id]);

  // Auto-expand expenses section when revenue is complete and expenses is not
  useEffect(() => {
    if (completionStatus.revenue && !completionStatus.expenses && !expandedSections.has('expenses')) {
      setExpandedSections(prev => {
        const newSet = new Set(prev);
        newSet.add('expenses');
        return newSet;
      });
    }
  }, [completionStatus.revenue, completionStatus.expenses]);

  const renderRevenueTab = () => (
    <div className="space-y-6">
      {/* TAM Projection Button */}
      <div className="p-4 bg-gradient-to-r from-fc-orange-50 to-orange-50 border border-fc-orange-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-semibold text-fc-orange-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              AI Financial Projections
            </h4>
            <p className="text-sm text-fc-orange-700 mt-1">
              Generate data-driven projections based on operational comparables and market data
            </p>
          </div>
        </div>
        <button
          onClick={handleTamProjectionClick}
          disabled={tamLoading}
          className="w-full bg-fc-orange-500 text-white py-3 px-4 rounded-lg hover:bg-fc-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium"
        >
          {tamLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Projections...
            </>
          ) : (
            <>
              <Lightbulb className="w-5 h-5" />
              Generate AI Projections
            </>
          )}
        </button>

        {/* Warning if no property selected from database */}
        {!data.stationObjectId && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Database Property Not Selected:</strong> You didn't select a property from our database in the Basic Info step. Projections will be less accurate without demographic and traffic data. Consider going back to Basic Info and selecting a property from the dropdown.
            </p>
          </div>
        )}

        {tamError && (
          <div className="mt-3 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-900 mb-1">
                  Unable to Generate Projections
                </h4>
                <p className="text-sm text-amber-800">{tamError}</p>
                <p className="text-xs text-amber-700 mt-2">
                  You can continue by entering values manually below.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TAM Diagnostics — internal/testing tool. Gated behind dev mode so it
          shows on `npm run dev` but never ships to production. */}
      {import.meta.env.DEV && tamResult && <TamDiagnostics result={tamResult} />}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          {tamResult && (
            <TamSuggestion
              fieldName="annualGallons"
              label="Fuel Volume"
              tamValue={tamResult.estimates.fuel_volume_gal}
              unit=""
              onAccept={() => {
                logTamAccept?.('annualGallons', tamResult.estimates.fuel_volume_gal, data.annualGallons || 0);
                shownTamFields.current.delete('annualGallons');
                onUpdate({ annualGallons: tamResult.estimates.fuel_volume_gal });
              }}
            />
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium text-gray-700">Fuel Volume</label>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">Annual</span>
            </div>
            <VolumeInput
              id="annualGallons"
              label=""
              tooltip="Total annual fuel volume sold."
              value={getPeriodValue(data.annualGallons)}
              onChange={handleFinancialInputChange('annualGallons')}
              placeholder="e.g., 1,200,000"
              unit="gal"
            />
          </div>
        </div>
        <div>
          {tamResult && (
            <TamSuggestion
              fieldName="fuelMargin"
              label="Fuel Margin"
              tamValue={tamResult.estimates.fuel_profit_margin_per_gal}
              unit=""
              onAccept={() => {
                logTamAccept?.('fuelMargin', tamResult.estimates.fuel_profit_margin_per_gal, data.fuelMargin || 0);
                shownTamFields.current.delete('fuelMargin');
                onUpdate({ fuelMargin: tamResult.estimates.fuel_profit_margin_per_gal });
              }}
            />
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium text-gray-700">Fuel Margin ($/gallon)</label>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">Annual Average</span>
            </div>
            <MoneyInput
              id="fuelMargin"
              label=""
              tooltip="Average profit per gallon of fuel over the year."
              value={data.fuelMargin}
              onChange={handleFinancialInputChange('fuelMargin')}
              placeholder="e.g., 0.30"
              error={!!(data.annualGallons && data.annualGallons > 0 && !data.fuelMargin)}
              hint={data.brandAffiliation === 'unbranded' ? 'Typical annual avg: $0.40 – $1.00/gal (unbranded). Use your 12-month average, not recent peak.' : 'Typical annual avg: $0.20 – $0.80/gal (branded). Use your 12-month average, not recent peak.'}
            />
          </div>
        </div>
        <div>
          {tamResult && (
            <TamSuggestion
              fieldName="storeRevenue"
              label="C-Store Revenue"
              tamValue={tamResult.estimates.cstore_sales}
              unit=""
              onAccept={() => {
                logTamAccept?.('storeRevenue', tamResult.estimates.cstore_sales, data.storeRevenue || 0);
                shownTamFields.current.delete('storeRevenue');
                onUpdate({ storeRevenue: tamResult.estimates.cstore_sales });
              }}
            />
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium text-gray-700">C-Store Revenue</label>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">Annual</span>
            </div>
            <MoneyInput
              id="storeRevenue"
              label=""
              tooltip="Total annual revenue from in-store sales."
              value={getPeriodValue(data.storeRevenue)}
              onChange={handleFinancialInputChange('storeRevenue')}
              placeholder="e.g., 800,000"
            />
          </div>
        </div>
        <div>
          {tamResult && (
            <TamSuggestion
              fieldName="storeMargin"
              label="C-Store Margin"
              tamValue={tamResult.estimates.cstore_margin_pct}
              unit=""
              onAccept={() => {
                logTamAccept?.('storeMargin', tamResult.estimates.cstore_margin_pct / 100, data.storeMargin || 0);
                shownTamFields.current.delete('storeMargin');
                onUpdate({ storeMargin: tamResult.estimates.cstore_margin_pct / 100 });
              }}
            />
          )}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium text-gray-700">C-Store Margin</label>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">Annual Average</span>
            </div>
            <PercentageInput
              id="storeMargin"
              label=""
              tooltip="Average profit margin on in-store sales over the year."
              value={data.storeMargin}
              onChange={handleMarginChange('storeMargin')}
              placeholder="e.g., 35%"
              error={!!(data.storeRevenue && data.storeRevenue > 0 && !data.storeMargin)}
              hint="Typical: 28% – 38%"
            />
          </div>
        </div>
      </div>

      {data.hasCarWash && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-3">Car Wash Revenue</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm font-medium text-gray-700">Car Wash Revenue</label>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">Annual</span>
              </div>
              <MoneyInput
                id="carWashRevenue"
                label=""
                tooltip="Total annual revenue from car wash services."
                value={getPeriodValue(data.carWashRevenue)}
                onChange={handleFinancialInputChange('carWashRevenue')}
                placeholder="e.g., 150,000"
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm font-medium text-gray-700">Car Wash Margin</label>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">Annual Average</span>
              </div>
              <PercentageInput
                id="carWashMargin"
                label=""
                tooltip="Average profit margin on car wash services over the year."
                value={data.carWashMargin}
                onChange={handleMarginChange('carWashMargin')}
                placeholder="e.g., 60%"
                error={!!(data.carWashRevenue && data.carWashRevenue > 0 && !data.carWashMargin)}
                hint={data.carWashType === 'self-service' ? 'Typical: 75% – 95% (in-bay / self-service)' : 'Typical: 40% – 70% (full service / tunnel)'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Additional Revenue Streams */}
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
        <h4 className="font-medium text-purple-900 mb-3">Additional Revenue (Repair Bays/Rent/Misc.)</h4>
        <p className="text-sm text-purple-700 mb-4">
          Optional additional revenue streams such as repair bay services, rental income, or miscellaneous operations. Enter annual revenue amounts.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <MoneyInput
            id="qsrRepairBayRevenue"
            label="Additional Revenue"
            tooltip="Annual revenue from repair bay services, rental income, or other additional revenue streams."
            value={getPeriodValue(data.qsrRepairBayRevenue)}
            onChange={handleFinancialInputChange('qsrRepairBayRevenue')}
            placeholder="e.g., 180,000"
          />
          <PercentageInput
            id="qsrRepairBayMargin"
            label="Additional Revenue Margin"
            tooltip="Profit margin on additional revenue streams."
            value={data.qsrRepairBayMargin}
            onChange={handleMarginChange('qsrRepairBayMargin')}
            placeholder="e.g., 65%"
            error={!!(data.qsrRepairBayRevenue && data.qsrRepairBayRevenue > 0 && !data.qsrRepairBayMargin)}
            hint="Typical: 40% – 60% (repair/service); varies for rent/misc."
          />
        </div>
      </div>

      {/* QSR Leased Fee Section */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Leased QSR (Quick Service Restaurant)
          </label>
          <Tooltip content="Indicate if the property includes a leased QSR operation for separate leased fee valuation">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <select
          value={data.hasLeasedQSR ? 'yes' : 'no'}
          onChange={(e) => onUpdate({ hasLeasedQSR: e.target.value === 'yes' })}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>

        {data.hasLeasedQSR && (
          <div className="space-y-4 mt-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
            <h4 className="font-semibold text-blue-900 mb-3">QSR Lease Details</h4>

            {/* Tenant Name */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Tenant Name
                </label>
                <Tooltip content="Select the QSR tenant operating at this location">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </Tooltip>
              </div>
              <select
                value={data.qsrTenantName || ''}
                onChange={(e) => onUpdate({ qsrTenantName: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Tenant</option>
                <option value="McDonald's">McDonald's</option>
                <option value="Jack in the Box">Jack in the Box</option>
                <option value="Subway">Subway</option>
                <option value="Taco Bell">Taco Bell</option>
                <option value="KFC">KFC</option>
                <option value="Burger King">Burger King</option>
                <option value="Starbucks">Starbucks</option>
                <option value="Dunkin'">Dunkin'</option>
                <option value="Local/Independent">Local/Independent</option>
                <option value="Other National Chain">Other National Chain</option>
              </select>
            </div>

            {/* Guaranty Type */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Guaranty Type
                </label>
                <Tooltip content="Corporate guaranty (parent company on the hook) typically prices ~25 bp tighter than a franchisee guaranty. If you're not sure, leave it on Unsure.">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </Tooltip>
              </div>
              <div className="flex flex-wrap gap-4">
                {([
                  { value: 'corporate', label: 'Corporate' },
                  { value: 'franchisee', label: 'Franchisee' },
                  { value: 'unsure', label: 'Unsure' },
                ] as const).map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="qsrGuarantyType"
                      value={opt.value}
                      checked={(data.qsrGuarantyType || 'unsure') === opt.value}
                      onChange={() => onUpdate({ qsrGuarantyType: opt.value })}
                      className="text-fc-orange-500 focus:ring-fc-orange-500"
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Drive Thru */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Drive Thru
                </label>
                <Tooltip content="Does the QSR have drive-thru service? This affects valuation significantly">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </Tooltip>
              </div>
              <select
                value={data.qsrHasDriveThru === undefined ? '' : data.qsrHasDriveThru ? 'yes' : 'no'}
                onChange={(e) => onUpdate({ qsrHasDriveThru: e.target.value === 'yes' })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Lease Term Section */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Lease Start Year
                  </label>
                  <Tooltip content="Year the current lease term started">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                <input
                  type="number"
                  min="1980"
                  max="2025"
                  value={data.qsrLeaseStartYear || ''}
                  onChange={(e) => onUpdate({ qsrLeaseStartYear: parseInt(e.target.value) || undefined })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 2020"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Lease End Year
                  </label>
                  <Tooltip content="Year the current lease term expires (leave blank if month-to-month)">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                <input
                  type="number"
                  min="2025"
                  max="2050"
                  value={data.qsrLeaseEndYear || ''}
                  onChange={(e) => onUpdate({ qsrLeaseEndYear: parseInt(e.target.value) || undefined })}
                  disabled={data.qsrIsMonthToMonth}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  placeholder="e.g., 2030"
                />
              </div>
            </div>

            {/* Month-to-Month Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="qsrMTM"
                checked={data.qsrIsMonthToMonth || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  onUpdate({
                    qsrIsMonthToMonth: isChecked,
                    qsrLeaseEndYear: isChecked ? undefined : data.qsrLeaseEndYear,
                    qsrTotalTerm: isChecked ? 0 : undefined
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="qsrMTM" className="ml-2 block text-sm text-gray-700">
                Month-to-Month (MTM) - No fixed term
              </label>
            </div>

            {/* Options Total */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Option Years (Total)
                </label>
                <Tooltip content="Total number of option years available to tenant after current term">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </Tooltip>
              </div>
              <input
                type="number"
                min="0"
                max="50"
                value={data.qsrOptionsTotal || ''}
                onChange={(e) => onUpdate({ qsrOptionsTotal: parseInt(e.target.value) || undefined })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., 10"
              />
            </div>

            {/* Financial Details */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Current Monthly Rent
                  </label>
                  <Tooltip content="Current monthly rent amount paid by tenant">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <input
                    type="text"
                    value={data.qsrCurrentMonthlyRent ? data.qsrCurrentMonthlyRent.toLocaleString() : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      onUpdate({ qsrCurrentMonthlyRent: parseInt(value) || undefined });
                    }}
                    className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 15,000"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Expense Type
                  </label>
                  <Tooltip content="NNN = Net-Net-Net (tenant pays all), MG = Modified Gross (landlord pays some)">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                <select
                  value={data.qsrExpenseType || ''}
                  onChange={(e) => onUpdate({ qsrExpenseType: e.target.value as 'NNN' | 'MG' })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Type</option>
                  <option value="NNN">NNN (Net-Net-Net)</option>
                  <option value="MG">MG (Modified Gross)</option>
                </select>
              </div>
            </div>

            {/* Annual Landlord Expenses */}
            {data.qsrExpenseType === 'MG' && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Annual Expenses (Landlord Incurs)
                  </label>
                  <Tooltip content="Annual expenses that landlord pays under Modified Gross lease">
                    <HelpCircle className="w-4 h-4 text-gray-400" />
                  </Tooltip>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <input
                    type="text"
                    value={data.qsrAnnualLandlordExpenses ? data.qsrAnnualLandlordExpenses.toLocaleString() : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      onUpdate({ qsrAnnualLandlordExpenses: parseInt(value) || undefined });
                    }}
                    className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 25,000"
                  />
                </div>
              </div>
            )}

            {/* Calculated Total Term Display */}
            {(data.qsrLeaseEndYear || data.qsrIsMonthToMonth) && (
              <div className="p-3 bg-blue-100 rounded-lg">
                <div className="text-sm font-medium text-blue-900">Calculated Total Term:</div>
                <div className="text-lg font-bold text-blue-900">
                  {data.qsrIsMonthToMonth
                    ? '0 years (Month-to-Month)'
                    : `${((data.qsrLeaseEndYear || 2025) - 2025) + (data.qsrOptionsTotal || 0)} years`
                  }
                </div>
                {!data.qsrIsMonthToMonth && (
                  <div className="text-xs text-blue-700 mt-1">
                    Remaining: {(data.qsrLeaseEndYear || 2025) - 2025} years + Options: {data.qsrOptionsTotal || 0} years
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gross Profit Indication */}
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <h4 className="font-medium text-yellow-900 mb-3">Total Gross Profit ({getPeriodLabel()})</h4>
        <MoneyInput
          id="totalGrossProfit"
          label="Total Gross Profit"
          tooltip="Total gross profit from all revenue streams. You can enter this directly or it will be calculated from the revenue fields."
          value={getPeriodValue(data.totalGrossProfit)}
          onChange={handleFinancialInputChange('totalGrossProfit')}
          placeholder="e.g., 750,000"
        />
        {!isManualGrossProfit && (
          <p className="text-sm text-yellow-700 mt-2">
            Auto-calculated from revenue streams above or can be manually entered above
          </p>
        )}
      </div>
    </div>
  );

  const renderProfitTab = () => (
    <div className="space-y-6">
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <h4 className="font-medium text-yellow-900 mb-3">Total Gross Profit ({getPeriodLabel()})</h4>
        <MoneyInput 
          id="totalGrossProfit" 
          label="Total Gross Profit" 
          tooltip="Total gross profit from all revenue streams. You can enter this directly or it will be calculated from the revenue fields." 
          value={getPeriodValue(data.totalGrossProfit)} 
          onChange={handleFinancialInputChange('totalGrossProfit')} 
          placeholder="e.g., 750,000" 
        />
        {!isManualGrossProfit && (
          <p className="text-sm text-yellow-700 mt-2">
            Auto-calculated from revenue streams above or can be manually entered above
          </p>
        )}
      </div>
    </div>
  );

  const renderExpensesTab = () => (
    <div className="space-y-6">
      <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="font-medium text-red-900">Total Operating Expenses ({getPeriodLabel()})</h4>
          <span className="text-xs bg-red-600 text-white px-2 py-1 rounded-full font-medium">REQUIRED</span>
        </div>
        {tamResult && (
          <TamSuggestion
            fieldName="operatingExpenses"
            label="Operating Expenses"
            tamValue={(data.totalGrossProfit || 0) * (tamResult.estimates.opex_over_gp_pct / 100)}
            unit=""
            onAccept={() => {
              const opexValue = (data.totalGrossProfit || 0) * (tamResult.estimates.opex_over_gp_pct / 100);
              logTamAccept?.('operatingExpenses', opexValue, data.operatingExpenses || 0);
              shownTamFields.current.delete('operatingExpenses');
              onUpdate({ operatingExpenses: opexValue });
            }}
          />
        )}
        <MoneyInput
          id="operatingExpenses"
          label="Total Operating Expenses"
          tooltip="Sum of all operating expenses. You can enter a total or fill out the fields below."
          value={getPeriodValue(data.operatingExpenses)}
          onChange={handleFinancialInputChange('operatingExpenses')}
          placeholder="e.g., 500,000"
        />
        <div className="mt-3 p-4 bg-amber-50 border border-amber-300 rounded-md">
          <p className="text-sm font-semibold text-amber-900 mb-2">⚠️ DO NOT include the following:</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {[
              'Officer / Owner Salary',
              'Depreciation',
              'Amortization',
              'Interest Expense',
              'Rent / Lease Payments',
              'Auto / Vehicle Expense',
              'One-Time / Non-Recurring Costs',
              'Income Tax Provision'
            ].map(item => (
              <div key={item} className="flex items-center gap-2 text-sm text-amber-900">
                <span className="text-red-500 font-bold">✕</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowExpenseExample(true)}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline font-medium"
          >
            Click here to see an example P&L
          </button>
        </div>

        {/* Typical Operating Expense Guide */}
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowOpexGuide(!showOpexGuide)}
            className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
          >
            <TrendingUp className="w-3.5 h-3.5" />
            {showOpexGuide ? 'Hide' : 'Show'} Typical Operating Expense Ranges
          </button>
        </div>

        {showOpexGuide && (
          <div className="mt-3 p-4 bg-gradient-to-b from-slate-50 to-slate-100 border border-slate-200 rounded-xl shadow-sm">
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              Typical Operating Expenses by Station Type
            </h4>
            <p className="text-xs text-slate-600 mb-4">
              Operating expenses as a percentage of total gross profit. Use these as a guide if you don't have actual figures.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Kiosk / High-Volume Self-Serve */}
              <div className="p-3 bg-white rounded-lg border border-slate-200 flex flex-col">
                <div className="text-xs font-semibold text-slate-700 mb-1">Kiosk / Self-Serve</div>
                <div className="text-xs text-slate-500 mb-2">Small footprint, fuel-focused, minimal staffing</div>
                <div className="text-lg font-bold text-blue-700">35% – 45%</div>
                <div className="text-[10px] text-slate-400">of gross profit</div>
                {(data.totalGrossProfit || 0) > 0 && (
                  <div className="mt-auto pt-2 border-t border-slate-100">
                    <div className="text-[10px] text-slate-500">Suggested expense:</div>
                    <div className="text-sm font-bold text-blue-800">
                      ${Math.floor((getPeriodValue(data.totalGrossProfit) || 0) * 0.40).toLocaleString()}
                    </div>
                    <button
                      type="button"
                      onClick={() => onUpdate({ operatingExpenses: Math.floor((getPeriodValue(data.totalGrossProfit) || 0) * 0.40) })}
                      className="mt-2 w-full text-xs bg-fc-orange-500 text-white py-1.5 px-3 rounded-md hover:bg-fc-orange-600 transition-colors font-medium"
                    >
                      Use This Value
                    </button>
                  </div>
                )}
              </div>

              {/* Regular Station */}
              <div className="p-3 bg-white rounded-lg border border-blue-200 ring-1 ring-blue-100 flex flex-col">
                <div className="text-xs font-semibold text-slate-700 mb-1">Regular Station</div>
                <div className="text-xs text-slate-500 mb-2">Standard c-store, moderate staffing & inventory</div>
                <div className="text-lg font-bold text-blue-700">45% – 55%</div>
                <div className="text-[10px] text-slate-400">of gross profit</div>
                {(data.totalGrossProfit || 0) > 0 && (
                  <div className="mt-auto pt-2 border-t border-slate-100">
                    <div className="text-[10px] text-slate-500">Suggested expense:</div>
                    <div className="text-sm font-bold text-blue-800">
                      ${Math.floor((getPeriodValue(data.totalGrossProfit) || 0) * 0.50).toLocaleString()}
                    </div>
                    <button
                      type="button"
                      onClick={() => onUpdate({ operatingExpenses: Math.floor((getPeriodValue(data.totalGrossProfit) || 0) * 0.50) })}
                      className="mt-2 w-full text-xs bg-fc-orange-500 text-white py-1.5 px-3 rounded-md hover:bg-fc-orange-600 transition-colors font-medium"
                    >
                      Use This Value
                    </button>
                  </div>
                )}
              </div>

              {/* Large C-Store / Food Service */}
              <div className="p-3 bg-white rounded-lg border border-slate-200 flex flex-col">
                <div className="text-xs font-semibold text-slate-700 mb-1">Large C-Store / Food Service</div>
                <div className="text-xs text-slate-500 mb-2">Full food program, high staffing, QSR operations</div>
                <div className="text-lg font-bold text-blue-700">55% – 65%</div>
                <div className="text-[10px] text-slate-400">of gross profit</div>
                {(data.totalGrossProfit || 0) > 0 && (
                  <div className="mt-auto pt-2 border-t border-slate-100">
                    <div className="text-[10px] text-slate-500">Suggested expense:</div>
                    <div className="text-sm font-bold text-blue-800">
                      ${Math.floor((getPeriodValue(data.totalGrossProfit) || 0) * 0.60).toLocaleString()}
                    </div>
                    <button
                      type="button"
                      onClick={() => onUpdate({ operatingExpenses: Math.floor((getPeriodValue(data.totalGrossProfit) || 0) * 0.60) })}
                      className="mt-2 w-full text-xs bg-fc-orange-500 text-white py-1.5 px-3 rounded-md hover:bg-fc-orange-600 transition-colors font-medium"
                    >
                      Use This Value
                    </button>
                  </div>
                )}
              </div>
            </div>
            <p className="mt-3 text-[10px] text-slate-400 leading-tight">
              Based on 2024–2025 industry survey data. Actual expenses vary by location, brand, labor market, and operating model. These ranges exclude owner salary, rent, depreciation, and other non-operating items.
            </p>
          </div>
        )}

        {/* Example P&L Popup */}
        {showExpenseExample && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowExpenseExample(false)}>
            <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setShowExpenseExample(false)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 text-lg font-bold"
              >
                ✕
              </button>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Example: Operating Expenses from a P&L</h3>
              <div className="space-y-2 text-sm font-mono">
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ Salaries & Wages (non-owner)</span><span>$185,000</span>
                </div>
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ Utilities</span><span>$42,000</span>
                </div>
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ Insurance</span><span>$28,000</span>
                </div>
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ Repairs & Maintenance</span><span>$35,000</span>
                </div>
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ Property Tax</span><span>$18,000</span>
                </div>
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ Merchant / CC Fees</span><span>$95,000</span>
                </div>
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ Advertising & Promotion</span><span>$8,000</span>
                </div>
                <div className="flex justify-between text-green-700 font-semibold">
                  <span>✓ General & Administrative</span><span>$22,000</span>
                </div>
                <div className="border-t border-gray-300 my-2" />
                <div className="flex justify-between text-red-500 line-through opacity-70">
                  <span>✕ Officer Salary</span><span>$120,000</span>
                </div>
                <div className="flex justify-between text-red-500 line-through opacity-70">
                  <span>✕ Depreciation</span><span>$65,000</span>
                </div>
                <div className="flex justify-between text-red-500 line-through opacity-70">
                  <span>✕ Amortization</span><span>$12,000</span>
                </div>
                <div className="flex justify-between text-red-500 line-through opacity-70">
                  <span>✕ Interest Expense</span><span>$48,000</span>
                </div>
                <div className="flex justify-between text-red-500 line-through opacity-70">
                  <span>✕ Rent / Lease</span><span>$0</span>
                </div>
                <div className="flex justify-between text-red-500 line-through opacity-70">
                  <span>✕ Auto / Vehicle</span><span>$6,500</span>
                </div>
                <div className="border-t-2 border-gray-400 my-2" />
                <div className="flex justify-between font-bold text-gray-900">
                  <span>Enter This Amount →</span><span>$433,000</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-500">
                Only include recurring, property-level operating expenses. Exclude owner compensation, financing costs, and non-cash accounting entries.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Net Operating Income Display */}
      {(data.totalGrossProfit || 0) > 0 && (data.operatingExpenses || 0) > 0 && (
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-green-900">Net Operating Income ({getPeriodLabel()})</h4>
              <p className="text-sm text-green-700 mt-1">Total Gross Profit - Total Operating Expenses</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-800">
                ${Math.floor((getPeriodValue(data.totalGrossProfit) || 0) - (getPeriodValue(data.operatingExpenses) || 0)).toLocaleString('en-US')}
              </div>
              <div className="text-sm text-green-600">
                ${Math.floor(getPeriodValue(data.totalGrossProfit) || 0).toLocaleString()} - ${Math.floor(getPeriodValue(data.operatingExpenses) || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Expense Breakdown — optional itemization. Only Total
          Operating Expenses (above) is required for the valuation. */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h4 className="font-medium text-gray-700">Itemized Expense Breakdown</h4>
          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full font-medium">OPTIONAL</span>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          You only need to provide Total Operating Expenses above. Filling these in
          gives a more detailed report but isn't required.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <MoneyInput
          id="salaries"
          label="Salaries"
          tooltip="Wages and benefits for all employees."
          value={getPeriodValue(data.salaries)}
          onChange={handleFinancialInputChange('salaries')}
          placeholder="e.g., 200,000"
        />
        <MoneyInput
          id="utilities"
          label="Utilities"
          tooltip="Electricity, water, gas, etc."
          value={getPeriodValue(data.utilities)}
          onChange={handleFinancialInputChange('utilities')}
          placeholder="e.g., 50,000"
        />
        <MoneyInput
          id="insurance"
          label="Insurance"
          tooltip="Liability, property, and other insurance costs."
          value={getPeriodValue(data.insurance)}
          onChange={handleFinancialInputChange('insurance')}
          placeholder="e.g., 30,000"
        />
        <MoneyInput
          id="repairsAndMaintenance"
          label="Repairs & Maintenance"
          tooltip="Costs for maintaining equipment and property."
          value={getPeriodValue(data.repairsAndMaintenance)}
          onChange={handleFinancialInputChange('repairsAndMaintenance')}
          placeholder="e.g., 25,000"
        />
        <MoneyInput
          id="propertyTax"
          label="Property Tax"
          tooltip="Annual property taxes."
          value={getPeriodValue(data.propertyTax)}
          onChange={handleFinancialInputChange('propertyTax')}
          placeholder="e.g., 40,000"
        />
        <MoneyInput
          id="merchantFees"
          label="Merchant Fees"
          tooltip="Credit card processing and other merchant fees."
          value={getPeriodValue(data.merchantFees)}
          onChange={handleFinancialInputChange('merchantFees')}
          placeholder="e.g., 60,000"
        />
        <MoneyInput
          id="advertisingAndPromotion"
          label="Advertising & Promotion"
          tooltip="Marketing and promotional expenses."
          value={getPeriodValue(data.advertisingAndPromotion)}
          onChange={handleFinancialInputChange('advertisingAndPromotion')}
          placeholder="e.g., 15,000"
        />
        <MoneyInput
          id="generalAndAdmin"
          label="General & Admin"
          tooltip="Other general and administrative expenses."
          value={getPeriodValue(data.generalAndAdmin)}
          onChange={handleFinancialInputChange('generalAndAdmin')}
          placeholder="e.g., 20,000"
        />
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Reporting Configuration</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Type</label>
            <select 
              value={reportingType} 
              onChange={(e) => handleTypeChange(e.target.value as ReportingType)} 
              className={inputClasses.base}
            >
              <option value="historical">Historical Data</option>
              <option value="projected">Projected Data</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Period</label>
            <select 
              value={reportingPeriod} 
              onChange={(e) => handlePeriodChange(e.target.value as ReportingPeriod)} 
              className={inputClasses.base}
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-3">Market Projections</h4>
        <button 
          onClick={handleProjectionClick} 
          disabled={isLoading} 
          className="w-full bg-fc-orange-500 text-white py-2 px-4 rounded-lg hover:bg-fc-orange-600 disabled:bg-gray-400 flex items-center justify-center gap-2"
        >
          {isLoading ? <Loader2 className="animate-spin" /> : 'Calculate Market Projections'}
        </button>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        {projections && (
          <div className="mt-4 space-y-2 text-sm">
            <p><strong>Market Status:</strong> <span className="font-mono p-1 bg-gray-200 rounded">{projections.market_status}</span></p>
            <p><strong>Location Quotient:</strong> {projections.location_quotient.toFixed(2)}</p>
            <p><strong>Est. Annual Fuel Volume:</strong> {projections.estimated_annual_fuel_volume.toLocaleString()} gal</p>
            {estimatedRevenue && <p><strong>Est. Annual Revenue:</strong> ${estimatedRevenue.estimatedAnnualRevenue.toLocaleString()}</p>}
          </div>
        )}
      </div>
    </div>
  );

  const renderSynergyContent = () => (
    <div className="space-y-6">
      <EntrepreneurialSynergyBox
        value={data.entrepreneurialSynergy || defaultSynergyAnswers}
        onChange={(synergy) => onUpdate({ entrepreneurialSynergy: synergy })}
        disabled={false}
        predictedValue={undefined} // Will be calculated on results page
      />
    </div>
  );

  const renderSectionContent = (sectionId: SectionType) => {
    switch (sectionId) {
      case 'revenue': return renderRevenueTab();
      case 'expenses': return renderExpensesTab();
      case 'synergy': return renderSynergyContent();
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Financial Metrics</h2>
            <p className="text-gray-600 mt-1">Enter your revenue, margins, and operating expenses. If you don't have exact figures, use our AI projections as a starting point. Not sure about margins? Toggle "Show Typical Industry Margins" below for common ranges.</p>
          </div>

          {allSectionsComplete && (
            <CheckCircle className="h-5 w-5 text-green-500" />
          )}
        </div>

        {/* Pre-payment expectations banner — sets the user up for a deliberate
            single-run flow before they click into the inputs. */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-semibold text-blue-900 mb-2">
            Quick heads-up before you fill these in
          </p>
          <ul className="text-sm text-blue-900 space-y-1.5 list-disc pl-5">
            <li>
              Your purchase covers <strong>one valuation run</strong>, so please
              review your numbers carefully — they can't be edited after you submit.
            </li>
            <li>
              If our AI Projections fill in estimates below, treat them as starting
              points. Feel free to <strong>override any or all</strong> with your
              own numbers if you have better data on this property.
            </li>
            <li>
              Only <strong>Total Operating Expenses</strong> is required — the
              itemized expense breakdown below it is optional.
            </li>
          </ul>
        </div>

        {/* Completion Alert */}
        {!allSectionsComplete && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-amber-600 mr-2" />
              <div className="text-sm text-amber-800">
                <strong>Complete all required sections for accurate valuation.</strong> Fill out Revenue Streams and Operating Expenses below.
              </div>
            </div>
          </div>
        )}

        {/* Success message when complete */}
        {allSectionsComplete && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <div className="text-sm text-green-800">
                <strong>All required sections complete!</strong> You can now view your valuation results. The Operating Advantages section is optional.
              </div>
            </div>
          </div>
        )}

        {/* Accordion Sections — inlined to prevent unmount/remount losing input focus */}
        <div className="space-y-4">
          {sections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSections.has(section.id);
            const isComplete = section.completed;

            return (
              <div
                key={section.id}
                className={clsx(
                  "border rounded-lg overflow-hidden transition-all duration-200",
                  isComplete && section.required ? "border-green-300 bg-green-50/30" : "border-gray-200",
                  !isComplete && section.required ? "border-amber-300" : ""
                )}
              >
                {/* Accordion Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={clsx(
                    "w-full px-6 py-4 flex items-center justify-between text-left transition-colors",
                    isExpanded ? "bg-gray-50" : "hover:bg-gray-50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={clsx(
                      "p-2 rounded-lg",
                      isComplete && section.required ? "bg-green-100" : section.required ? "bg-blue-100" : "bg-gray-100"
                    )}>
                      <Icon className={clsx(
                        "w-5 h-5",
                        isComplete && section.required ? "text-green-600" : section.required ? "text-blue-600" : "text-gray-600"
                      )} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{section.label}</h3>
                        {section.required ? (
                          isComplete ? (
                            <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Complete
                            </span>
                          ) : (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                              Required
                            </span>
                          )
                        ) : (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            Optional
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{section.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Accordion Content */}
                {isExpanded && (
                  <div className="px-6 py-6 border-t border-gray-200 bg-white">
                    {renderSectionContent(section.id)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Next Section Prompt - shows when revenue is complete but expenses is not */}
        {completionStatus.revenue && !completionStatus.expenses && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="text-sm text-blue-800">
                <strong>Revenue section complete!</strong> Now enter your Operating Expenses below to continue.
              </div>
              {!expandedSections.has('expenses') && (
                <button
                  onClick={() => {
                    setExpandedSections(prev => {
                      const newSet = new Set(prev);
                      newSet.add('expenses');
                      return newSet;
                    });
                    // Scroll to expenses section
                    setTimeout(() => {
                      document.getElementById('expenses-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                >
                  Open Expenses Section
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Insufficient Data Modal */}
      <InsufficientDataModal
        isOpen={showInsufficientDataModal}
        onClose={() => setShowInsufficientDataModal(false)}
        propertyAddress={data.location}
        stationObjectId={data.stationObjectId}
      />
    </div>
  );
}
